<template>
  <div>
    <el-dialog
      width="632px"
      :visible="dialogVisible"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
    >
      <div slot="title">
        <div class="el-dialog__title">{{ title }}</div>
      </div>
      <div class="content">
        <div v-if="errorsCounter" class="errors">
          <div class="error-info">
            <div class="icon">
              <i class="el-icon-warning-outline"></i>
            </div>
            <div>
              <div class="title">
                {{
                  $t('CampaignForm_ReviewDialog_Error_Title', {
                    number: errorsCounter
                  })
                }}
              </div>
              <div class="desc">
                {{ $t('CampaignForm_ReviewDialog_Error_Desc') }}
              </div>
            </div>
          </div>
          <div class="error-block" v-for="(errorList, name) in errorMessages">
            <div class="item-name">
              {{ getItemName(name) }}
            </div>
            <ul class="item-errors">
              <li v-for="item in errorList">{{ item }}</li>
            </ul>
          </div>
        </div>
        <div v-else>
          <div
            v-if="
              askToCalculateRecipients &&
                !campaignSaveAsTemplate &&
                !isTemplateCampaign
            "
            class="calculate-info-label"
          >
            <i class="el-icon-info" />
            {{ $t('CampaignForm_ReviewDialog_RecipientCount_Label') }}
            <a
              href="https://docs.xtremepush.com/docs/engagement-rules-categories#require-audience-count-on-campaign-approval"
              target="_blank"
            >
              {{
                $t('CampaignForm_ReviewDialog_RecipientCount_Label_LearnMore')
              }}
            </a>
          </div>
          <schedule-info
            :key="key"
            ref="info"
            :model="value"
            v-model="recipientsObject"
            dialog
            :recipients="recipients"
            @calculate="calculateRecipients"
            @check="$emit('check')"
            :campaign-save-as-template="campaignSaveAsTemplate"
          />
          <div class="info">
            <div class="label">
              {{ $t('CampaignForm_ReviewDialog_CampaignType') }}
            </div>
            <div class="msg">
              {{
                value.type !== campaignTypeEnum.TYPE_WORKFLOW
                  ? $t('Campaign_Dialog_Type_SingleStage')
                  : $t('Campaign_Dialog_Type_MultiStage')
              }}
            </div>
          </div>
          <div class="info">
            <div class="label">
              {{ $t('CampaignForm_ReviewDialog_Channels') }}
            </div>
            <div class="msg">{{ channels }}</div>
          </div>
          <template v-if="showRecipientsList || askToCalculateRecipients">
            <div class="info">
              <div class="label">
                {{ $t('CampaignForm_ReviewDialog_Recipients') }}
              </div>
              <div
                class="msg"
                v-if="recipientsObject && Object.keys(recipientsObject).length"
              >
                <template v-for="(res, ch) in recipientsObject">
                  <div>{{ $t(getChannelLabel(ch)) }}: {{ res }}</div>
                </template>
              </div>
              <div v-else class="msg">–</div>
            </div>
          </template>

          <div class="info">
            <div class="label">
              {{ $t('CampaignForm_ReviewDialog_MultiLang') }}
            </div>
            <div class="msg">
              <div v-for="lang in langList">
                {{ lang }}
              </div>
            </div>
          </div>
          <div
            v-if="
              value.type === campaignTypeEnum.TYPE_WORKFLOW &&
                value.status === 2 &&
                !campaignSaveAsTemplate
            "
            class="running-workflow-update"
            v-html="$t('CampaignForm_ReviewDialog_WorkflowUpdate')"
          />
          <div
            v-if="
              value.type === campaignTypeEnum.TYPE_WORKFLOW &&
                value.status === 3
            "
            class="running-workflow-update"
            v-html="$t('CampaignForm_ReviewDialog_WorkflowUpdate_Paused')"
          />
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          @click="handleClose"
          data-qa="close-launch-dialog-btn"
        >
          <template v-if="errors.status">
            {{ $t('CampaignForm_ReviewDialog_Btn_Cancel') }}
          </template>
          <template v-else>
            {{ $t('CampaignForm_ReviewDialog_Btn_KeepEditing') }}
          </template>
        </el-button>
        <span @click="showError">
          <el-button
            type="primary"
            @click="checkLastEditedAndHandleSave"
            :disabled="!!errorsCounter || disableSaveBtn || isLaunching"
            :loading="loading"
            data-qa="primary-action-dialog-btn"
          >
            <i class="ico icon-check"></i>
            {{ saveBtnLabel }}
          </el-button>
        </span>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import {
  channelLabels as labels,
  channelTypes as types
} from '@/modules/campaign-manage/enum/Labels'
import { languages } from '@/modules/campaign-manage/enum/LanguagesEnum'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import ScheduleInfo from '@/modules/campaign-manage/components/review/ScheduleInfo'
import { campaignSendTypeEnum } from '@/modules/campaigns/enum/CampaignSendTypeEnums'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { $hasFeature } from '@/core/utils/VueAcl'
import CreateCampaignMixin from '@/modules/campaign-manage/mixins/CreateCampaignMixin'
import { MessageTypeEnum } from '@/common/enum/MessageTypeEnum'

export default {
  name: 'ReviewDialog',
  components: { ScheduleInfo },
  mixins: [CreateCampaignMixin],
  props: {
    dialogVisible: {
      type: Boolean,
      required: true
    },
    initialStateModel: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      required: true
    },
    value: {
      type: Object,
      required: true
    },
    recipients: {
      type: Object,
      required: true
    },
    lastModifiedData: {
      type: Object,
      required: false,
      default: null
    },
    campaignSaveAsTemplate: {
      type: Boolean,
      required: false,
      default: false
    },
    idempotencyKey: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      isLaunching: false,
      disableSaveBtn: false,
      key: '',
      recipientsObject: {},
      loading: false,
      localModel: {},
      campaignTypeEnum,
      channelTypes: { ...types, ...{ workflow: 12 } },
      channelLabels: {
        ...labels,
        workflow: 'CampaignForm_ChannelsSelect_Workflow',
        inapp: 'CampaignForm_ChannelsSelect_Inapp',
        promotion: 'CampaignForm_ChannelsSelect_Promotion'
      },
      languages
    }
  },
  computed: {
    isLive() {
      return this.value.active === 1
    },
    isTemplateCampaign() {
      return (
        this.$route.name === 'CreateTemplateCampaign' ||
        this.$route.name === 'UpdateTemplateCampaign' ||
        this.$route.name === 'CopyTemplateCampaign'
      )
    },
    isPast() {
      return (
        this.value.hasOwnProperty('status_canonical') &&
        this.value.status_canonical === 'past'
      )
    },
    title() {
      let title = ''

      if (this.value.postValidateDraftStatus) {
        title = this.$t('CampaignForm_ReviewDialog_Title_Draft')
      } else if (
        this.value.is_campaign_template ||
        this.campaignSaveAsTemplate
      ) {
        title = this.$t('CampaignForm_ReviewDialog_Title_SaveCampaignTemplate')
      } else {
        title = this.value.id
          ? this.$t('CampaignForm_ReviewDialog_Title_Update')
          : this.$t('CampaignForm_ReviewDialog_Title_Launch')
      }

      return this.value.title ? `${title} "${this.value.title}"` : `${title}`
    },
    askToCalculateRecipients() {
      let campaignTypes = [
        campaignSendTypeEnum.SEND_TYPE_NOW,
        campaignSendTypeEnum.SEND_TYPE_FUTURE,
        campaignSendTypeEnum.SEND_TYPE_SCHEDULE
      ]
      if (this.value.trigger || !campaignTypes.includes(this.value.send_type)) {
        return false
      }

      if (this.value.type === campaignTypeEnum.TYPE_WORKFLOW) {
        return true
      }

      return this.showRecipientsList
    },
    showRecipientsList() {
      const channels = Object.keys(this.value.messages).map(key =>
        parseInt(key)
      )
      return (
        this.$store.state.app.project.calculate_recipients ||
        channels.includes(this.channelTypes.sms) ||
        channels.includes(this.channelTypes.whatsapp)
      )
    },
    channels() {
      let channels = []
      if (this.value.promotion) {
        channels.push(this.$t('CampaignForm_ChannelsSelect_Promotion'))
      }
      Object.keys(this.value.messages).forEach(index =>
        channels.push(this.$t(this.getChannelLabel(index)))
      )
      return channels.join(', ') || '–'
    },
    langList() {
      if (!this.value.languages_multi) {
        return ['–']
      }
      return this.value.languages_list.map(
        item => `${languages[item]} (${item})`
      )
    },
    errorMessages() {
      if (!this.errorsCounter) {
        return []
      }
      let items = {}
      Object.keys(this.errors).forEach(item => {
        let msg = []
        if (
          this.value.ab ||
          this.value.languages_multi ||
          item === 'send_time'
        ) {
          msg = this.errors[item]
        } else {
          msg = this.errors[item].map(err => err.split(':').pop()) // remove item name from error msg
        }
        let path = item.match(/[^\][.]+/g)
        let index =
          path[0] === 'messages' && path[1]
            ? this.getChannelLabel(path[1]) // messages[1][push_text] -> iOS Push
            : path[0] // content_fetch_sources[0][url] -> content_fetch_sources
        _.set(
          items,
          index,
          items[index] ? [...items[index], ...msg].sort() : msg.sort()
        )
      })
      return items
    },
    errorsCounter() {
      return this.errors.length || Object.keys(this.errors).length
    },
    saveBtnLabel() {
      if (this.value.is_campaign_template || this.campaignSaveAsTemplate) {
        return this.$t('CampaignForm_ReviewDialog_Btn_SaveTemplateCampaign')
      }

      if (this.value.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE) {
        return this.$t('CampaignStepped_Btn_SaveOptimoveTemplate')
      }

      if (this.value.postValidateDraftStatus) {
        return this.$t('CampaignForm_ReviewDialog_Btn_Save')
      } else if (this.isLive || this.isPast) {
        return this.$t('CampaignForm_ReviewDialog_Btn_Update')
      } else {
        return this.$t('CampaignForm_ReviewDialog_Btn_Launch')
      }
    },
    saveEndpoint() {
      if (this.localModel.id) {
        return projectEndpoint({
          method: 'patch',
          url: `campaigns/${this.localModel.id}`,
          data: this.localModel,
          headers: {
            'Idempotency-Key': this.localModel.id
          }
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: 'campaigns',
          data: this.localModel,
          headers: {
            'Idempotency-Key': this.idempotencyKey
          }
        })
      }
    }
  },
  watch: {
    'recipients.polling': {
      handler(polling, oldPolling) {
        if (oldPolling && !polling) {
          this.disableSaveBtn = false
        }
      }
    },
    dialogVisible() {
      if (this.dialogVisible) {
        this.key = new Date().getTime()
        this.disableSaveBtn = !!(
          !this.value.postValidateDraftStatus &&
          !this.value.is_campaign_template &&
          !this.campaignSaveAsTemplate &&
          this.askToCalculateRecipients
        )
      }
    }
  },
  methods: {
    applyLastUsed() {
      return RestApi(
        projectEndpoint({
          method: 'patch',
          url: `campaigns/${this.$route.query.id}`,
          data: {
            campaign_template_last_used: Math.floor(Date.now() / 1000)
          },
          headers: {
            'Idempotency-Key': this.$route.query.id
          }
        })
      )
    },
    showError() {
      if (this.disableSaveBtn) {
        this.$message({
          message: this.$t('CampaignForm_ReviewDialog_RecalculateWarning'),
          type: 'error'
        })
      }
    },
    getItemName(str) {
      return this.$te(`CampaignForm_ReviewDialog_Field_${str}`)
        ? this.$t(`CampaignForm_ReviewDialog_Field_${str}`)
        : this.$t(str)
    },

    getChannelLabel(ch) {
      const types = _.invert(this.channelTypes)
      return _.get(this.channelLabels, types[ch])
    },
    handleClose() {
      this.$emit('update:dialogVisible', false)

      if (this.askToCalculateRecipients) {
        this.$emit('interrupt')
      }
    },
    async handleSave() {
      this.loading = true
      this.localModel = _.cloneDeep(this.value)
      this.localModel.draft = this.value.postValidateDraftStatus
      if (this.$route.name === 'CopyCampaign') {
        this.localModel.copied_from = this.$route.query.id
      }
      if ($hasFeature('campaignTemplates') && this.campaignSaveAsTemplate) {
        this.localModel.id = null
        this.localModel.is_campaign_template = 1
        this.localModel.status = 0
        this.localModel.is_open = 0
        if (
          this.localModel.type === campaignTypeEnum.TYPE_WORKFLOW &&
          this.$route.query.id
        ) {
          await RestApi(
            projectEndpoint({
              method: 'post',
              url: `campaigns/copy-workflow`,
              params: {
                campaign_id: this.$route.query.id
              }
            })
          )
            .then(({ data }) => {
              if (data.length > 0) {
                const variantKeys = Object.keys(
                  this.localModel.messages[MessageTypeEnum.WORKFLOW]
                )
                variantKeys.forEach((key, index) => {
                  if (data[index]) {
                    // prettier-ignore
                    this.localModel.messages[MessageTypeEnum.WORKFLOW][key]['content']['workflow_id'] = data[index].id
                  } else {
                    // prettier-ignore
                    this.localModel.messages[MessageTypeEnum.WORKFLOW][key]['content']['workflow_id'] = null
                  }
                })
              }
            })
            .catch(error => {
              this.$message({
                message: error.message,
                type: 'error'
              })
            })
        }
      }

      this.formatModelDates()
      this.setVariantProps()
      this.setLanguageProps()
      this.setLocations()
      this.setTargetApplications()
      this.checkEventsData()
      if (
        this.$route.name === 'CreateFromTemplate' &&
        !this.campaignSaveAsTemplate
      ) {
        this.applyLastUsed()
      }
      RestApi(this.saveEndpoint)
        .then(({ data }) => {
          this.$emit('update:initialStateModel', this.value)
          this.afterSave(data)
        })
        .catch(() => {
          this.$message.error(this.$t('CampaignForm_ReviewDialog_Error_Save'))
          this.handleClose()
        })
        .finally(() => {
          this.loading = false
          this.isLaunching = false
        })
    },
    afterSave(data) {
      this.handleClose()

      if (this.value.is_campaign_template || this.campaignSaveAsTemplate) {
        this.$router.push(projectRoute({ name: 'TemplateCampaignsTab' }))
        return
      }

      if (
        !this.value.postValidateDraftStatus &&
        !this.value.is_campaign_template &&
        this.value.send_type === campaignSendTypeEnum.SEND_TYPE_NOW
      ) {
        this.$router.push(
          projectRoute({ name: 'CampaignExecutions', query: { id: data.id } })
        )
      } else {
        this.$router.push(projectRoute({ name: 'AllCampaignsTab' }))
      }
    },
    resetSelection() {
      this.selectedTemplate = null
    },
    calculateRecipients(samplingAllowed) {
      this.$emit('calculate', samplingAllowed)
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-dialog__body {
  max-height: 430px;
  overflow: auto;
}

.errors {
  .error-info {
    display: flex;
    align-items: center;
    padding: 20px;
    background: #fde8e9;
    border-radius: 4px;

    .icon {
      padding-right: 16px;
      font-size: 24px;
      color: #ed1c25;
    }

    .title {
      color: #ed1c25;
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
    }

    .desc {
      font-size: 14px;
      line-height: 22px;
      color: #f4777c;
    }
  }

  .error-block {
    border: 1px solid #dbdee7;
    padding: 20px;
    border-radius: 4px;
    margin-top: 20px;

    .item-name {
      font-weight: 600;
      font-size: 14px;
      line-height: 22px;
      color: #5c6374;

      &:first-letter {
        text-transform: uppercase;
      }
    }

    .item-errors {
      font-size: 14px;
      line-height: 22px;
      color: #f14951;
      list-style: disc !important;
      padding-left: 18px;

      li {
        word-break: normal;
      }

      li:first-letter {
        text-transform: uppercase;
      }
    }
  }
}

.info {
  display: flex;
  padding-top: 16px;

  .label {
    min-width: 175px;
    width: 175px;
    font-weight: 600;
    font-size: 14px;
    padding-right: 16px;
  }

  .msg {
    color: #7e8596;

    &:first-letter {
      text-transform: uppercase;
    }
  }
}

.running-workflow-update {
  padding-top: 16px;
  word-break: break-word;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;

  .cancel-btn {
    margin-left: 0;
  }
}

/deep/ .el-dialog__title {
  max-width: 550px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/deep/ .el-dialog__header {
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 20px;
}

/deep/ .el-dialog__footer {
  border-top: 1px solid #ebeef5;
  padding: 20px;
}

/deep/ .el-button {
  min-width: 0 !important;
}

.calculate-info-label {
  margin: -15px 5px;
  font-size: 12px;
  color: #909399;

  i {
    margin-left: -5px;
    margin-bottom: 5px;
  }

  a {
    color: #1d87c8;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/ReviewDialog.vue